/*!

=========================================================
* Argon Dashboard React - v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {useState, useEffect} from "react";
import { useLocation, Route, Switch, Redirect } from "react-router-dom";
// reactstrap components
import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import Select from 'react-select';
// core components
import AdminNavbar from "components/Navbars/AdminNavbar";
import AdminFooter from "components/Footers/AdminFooter";
import Sidebar from "components/Sidebar/Sidebar";
import routes from "clientRoutes";
import { connect, useDispatch } from "react-redux";
import Plans from "views/pages/Plans";

import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { studentsLifetimePlan as studentsLifetimePlans } from "util/DataList";
import { fetchLtpItem } from "redux/ManageLifeTimePlan/ManageLifeTimePlanActions";
import { isTrialValid, getSpecialtyFields } from "config/subscriptionPlans";
import { getSpecialty } from "config/specialtyConfig";
import { startFreeTrial, startTrialReset } from "redux/Trial/TrialActions";
import { getUserData } from "redux/UserAuthentication/LoginActions";
import { redeemGroupCode, redeemGroupCodeReset, confirmIndividualCodePayment } from "redux/GroupOrders/GroupOrdersActions";
import { updateUserProfile } from "redux/Users/UsersActions";
import { customDropdown } from "util/reactCustomStyles";
import useCustomPmrPrograms from "util/useCustomPmrPrograms";
import Swal from 'sweetalert2';
const Admin = (props) => {
  const dispatch = useDispatch();
  
  let studentsLifetimePlan = studentsLifetimePlans

  useEffect(() => {
    dispatch(fetchLtpItem())
  }, [])

  useEffect(() => {

    if (props.fetchLtpItem.ltpitems.length) {
      const matchedItem = props.fetchLtpItem.ltpitems.find(
        (item) => props.userDetails.email === item.email
      );
      if (matchedItem) {
        studentsLifetimePlan.push(props.userDetails.email)
        setSubscribe("unlock")
      }
    }

  }, [props.fetchLtpItem]);

  const history = useHistory()
  const mainContent = React.useRef(null);
  const location = useLocation();

  // Handle post-registration action
  const [pendingTrial, setPendingTrial] = useState(false);
  const [pendingGroupCodeRedeem, setPendingGroupCodeRedeem] = useState(false);

  useEffect(() => {
    const postRegAction = sessionStorage.getItem('postRegAction');

    // Check for pending individual code payment (from registration)
    const pendingIndividualCode = sessionStorage.getItem('pendingIndividualCode');
    const pendingIndividualPiId = sessionStorage.getItem('pendingIndividualPaymentIntentId');
    if (pendingIndividualCode && pendingIndividualPiId) {
      sessionStorage.removeItem('pendingIndividualCode');
      sessionStorage.removeItem('pendingIndividualPaymentIntentId');
      if (postRegAction) sessionStorage.removeItem('postRegAction');
      // Confirm the individual code payment on backend
      dispatch(confirmIndividualCodePayment(pendingIndividualCode, pendingIndividualPiId))
        .then((data) => {
          dispatch(getUserData({ onLogin: true }));
          if (data.activated) {
            Swal.fire({
              title: 'Subscription Activated!',
              text: data.message,
              icon: 'success',
              confirmButtonText: 'Get Started',
              confirmButtonColor: '#2dce89',
            }).then(() => {
              history.push('/client/question-bank');
            });
          } else {
            Swal.fire({
              title: 'Payment Confirmed!',
              text: data.message,
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: '#5e72e4',
            }).then(() => {
              history.push('/client/plans');
            });
          }
        })
        .catch((err) => {
          console.error('Individual code confirmation error:', err);
          Swal.fire('Payment Error', 'There was an issue confirming your payment. Please contact support.', 'error');
        });
      return;
    }

    // Check for pending group code
    const pendingCode = sessionStorage.getItem('pendingGroupCode');
    if (pendingCode) {
      sessionStorage.removeItem('pendingGroupCode');
      // If there's a group code, skip the trial and redeem the code instead
      if (postRegAction) sessionStorage.removeItem('postRegAction');
      setPendingGroupCodeRedeem(true);
      dispatch(redeemGroupCode(pendingCode));
      return;
    }

    if (!postRegAction) return;

    sessionStorage.removeItem('postRegAction');

    if (postRegAction === 'buyPlan') {
      history.push('/client/plans');
    } else if (postRegAction === 'startTrial') {
      setPendingTrial(true);
      dispatch(startFreeTrial());
    }
  }, []);

  // Wait for trial API to succeed, then refresh user data and redirect
  useEffect(() => {
    if (!pendingTrial) return;

    if (props.trial?.success) {
      setPendingTrial(false);
      dispatch(startTrialReset());
      dispatch(getUserData({ onLogin: true }));
      Swal.fire({
        title: 'Free Trial Started!',
        text: 'You have 14 days of full access. Enjoy!',
        icon: 'success',
        timer: 3000
      }).then(() => {
        history.push('/client/question-bank/create-test');
      });
    }

    if (props.trial?.error) {
      setPendingTrial(false);
      dispatch(startTrialReset());
      Swal.fire({
        title: 'Error',
        text: props.trial.error,
        icon: 'error',
      });
    }
  }, [props.trial, pendingTrial]);

  // Handle group code redemption result
  useEffect(() => {
    if (!pendingGroupCodeRedeem) return;

    if (props.redeemGroupCode?.success) {
      setPendingGroupCodeRedeem(false);
      dispatch(redeemGroupCodeReset());
      dispatch(getUserData({ onLogin: true }));
      Swal.fire({
        title: 'Code Redeemed!',
        text: `Your subscription has been activated for ${props.redeemGroupCode.data?.months || ''} months.`,
        icon: 'success',
      }).then(() => {
        history.push('/client/question-bank/create-test');
      });
    }

    if (props.redeemGroupCode?.error) {
      setPendingGroupCodeRedeem(false);
      dispatch(redeemGroupCodeReset());
      Swal.fire({
        title: 'Code Error',
        text: props.redeemGroupCode.error?.error || 'Failed to redeem code. You can try again from the Plans page.',
        icon: 'error',
      }).then(() => {
        // Still start trial as fallback
        setPendingTrial(true);
        dispatch(startFreeTrial());
      });
    }
  }, [props.redeemGroupCode, pendingGroupCodeRedeem]);

  // Profile completion modals
  const allPmrProgramOptions = useCustomPmrPrograms();
  const yearOptions = [
    { value: 'Fellow', label: 'Fellow' },
    { value: 'PGY 1', label: 'PGY 1' },
    { value: 'PGY 2', label: 'PGY 2' },
    { value: 'PGY 3', label: 'PGY 3' },
    { value: 'PGY 4', label: 'PGY 4' }
  ];
  const heardAboutUsOptions = [
    { value: 'Residency Program', label: 'Residency Program' },
    { value: 'Co-Resident', label: 'Co-Resident' },
    { value: 'Attending Physician', label: 'Attending Physician' },
    { value: 'Social Media', label: 'Social Media' },
    { value: 'Google Search', label: 'Google Search' },
    { value: 'Conference/Event', label: 'Conference/Event' },
    { value: 'Email', label: 'Email' },
    { value: 'Other', label: 'Other' }
  ];

  const [showResidencyModal, setShowResidencyModal] = useState(false);
  const [showHeardModal, setShowHeardModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedHeard, setSelectedHeard] = useState(null);
  const [heardOther, setHeardOther] = useState('');
  const [needsHeardAboutUs, setNeedsHeardAboutUs] = useState(false);
  const profilePromptsCheckedRef = React.useRef(false);
  const handle = props.userDetails?.handle || props.userDetails?.email;

  useEffect(() => {
    if (!handle || profilePromptsCheckedRef.current) return;

    const ud = props.userDetails;
    if (ud.admin === true || ud.admin === 'true' || ud.coach === true || ud.coach === 'true') {
      profilePromptsCheckedRef.current = true;
      return;
    }

    const storageKey = `profilePrompts_${handle}`;
    if (sessionStorage.getItem(storageKey)) {
      profilePromptsCheckedRef.current = true;
      return;
    }

    const fields = getSpecialtyFields(props.activeSpecialty);
    const isPmr = props.activeSpecialty === 'pmr';
    const missingResidencyOrYear = isPmr && (!ud[fields.pmrProgram] || !ud[fields.year]);
    const missingHeard = !ud.heardAboutUs;

    if (!missingResidencyOrYear && !missingHeard) {
      profilePromptsCheckedRef.current = true;
      return;
    }

    profilePromptsCheckedRef.current = true;
    sessionStorage.setItem(storageKey, 'true');
    setNeedsHeardAboutUs(missingHeard);

    if (missingResidencyOrYear) {
      if (ud[fields.pmrProgram]) setSelectedProgram(allPmrProgramOptions.find(o => o.value === ud[fields.pmrProgram]) || null);
      if (ud[fields.year]) setSelectedYear(yearOptions.find(o => o.value === ud[fields.year]) || null);
      setShowResidencyModal(true);
    } else if (missingHeard) {
      setShowHeardModal(true);
    }
  }, [handle]);

  const handleResidencySave = () => {
    const ud = props.userDetails;
    const fields = getSpecialtyFields(props.activeSpecialty);
    const data = { handle: ud.handle };
    if (selectedProgram?.value) data[fields.pmrProgram] = selectedProgram.value;
    if (selectedYear?.value) data[fields.year] = selectedYear.value;

    setShowResidencyModal(false);

    if (data[fields.pmrProgram] || data[fields.year]) {
      dispatch(updateUserProfile(data, { silent: true })).then(() => {
        dispatch(getUserData({ onLogin: true }));
        Swal.fire({ title: 'Profile Updated!', icon: 'success', timer: 2000, showConfirmButton: false }).then(() => {
          if (needsHeardAboutUs) setShowHeardModal(true);
        });
      });
    } else {
      if (needsHeardAboutUs) setShowHeardModal(true);
    }
  };

  const handleResidencySkip = () => {
    setShowResidencyModal(false);
    if (needsHeardAboutUs) setShowHeardModal(true);
  };

  const handleHeardSave = () => {
    const ud = props.userDetails;
    let value = '';
    if (selectedHeard?.value === 'Other') {
      value = heardOther.trim() || 'Other';
    } else {
      value = selectedHeard?.value || '';
    }

    setShowHeardModal(false);

    if (value) {
      dispatch(updateUserProfile({ handle: ud.handle, heardAboutUs: value }, { silent: true })).then(() => {
        dispatch(getUserData({ onLogin: true }));
        Swal.fire({ title: 'Profile Updated!', icon: 'success', timer: 2000, showConfirmButton: false });
      });
    }
  };

  const [isLoading, setLoading] = useState(true)
  const [isUAS, setUAS] = useState("")
  const [isStudent, setStudent] = useState()

  const [showUpgradeMessage, setShowUpgradeMessage] = useState(true)

  useEffect(() => {
    if(props.userDetails.coach === true || props.userDetails.coach == "true" ){
      setStudent(false)
      setLoading(false)
    }
    if(props.userDetails.admin === true || props.userDetails.admin == "true" ){
      setStudent(false)
      setLoading(false)
    }
  },[props.userDetails])

  useEffect(() => {
  
    const lockPages = ['mcat-powerbook','chatbot', 'ideas', 'plans', 'pmr-review-guide', 'anki-decks'];
    
    if(lockPages.includes(history.location.pathname.split('/')[2])){
      setShowUpgradeMessage(false)
    } 
    else{
      setShowUpgradeMessage(true)
    }

    // Add a listener for changes
    const unlisten = history.listen((location) => {


      if(lockPages.includes(history.location.pathname.split('/')[2])){
        setShowUpgradeMessage(false)
      } 
      else{
        setShowUpgradeMessage(true)
      }
    });

    // Clean up the listener when the component is unmounted
    return () => {
      unlisten();
    };
  }, [history]);
  

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainContent.current.scrollTop = 0;
  }, [location]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/client") {
        
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (
        props.location.pathname.indexOf(routes[i].layout + routes[i].path) !==
        -1
      ) {
        return routes[i].name;
      }
    }
    return "Brand";
  };

  const [subscribe, setSubscribe] = useState('lock');


  const fetchSubscriptionDetails = () => {
    const uasField = getSpecialtyFields(props.activeSpecialty).ultimateAdvisingStudent;
    if(props.userDetails[uasField] !== undefined){
      if(props.userDetails[uasField] == "true" || props.userDetails[uasField] === true || studentsLifetimePlan.includes(props.userDetails.email)){

        setSubscribe('unlock')
        setLoading(false)

      }
      else{

        if(props.fetchDetailsSubscription.fetchdetailssubscription.length){

          const subsDetails = props.fetchDetailsSubscription.fetchdetailssubscription[0]

          if(subsDetails.status == "active" || subsDetails.status == "trialing"){
            setSubscribe('unlock')
            setLoading(false)
          }
          else{
            const date = new Date();
            const secondsSinceEpoch = Math.floor(date.getTime() / 1000);


            if(subsDetails.cancel_at_period_end){
              if(secondsSinceEpoch > subsDetails.canceled_at){
                setSubscribe('lock')
                 setLoading(false)
              }
              else{
                setSubscribe('unlock')
                 setLoading(false)
              }
            }
            else{
              setSubscribe('lock')
              setLoading(false)
            }
          }

        }
        // Check for free trial if no subscription
        else if(props.userDetails[getSpecialtyFields(props.activeSpecialty).trialStartDate]){
          const trialValid = isTrialValid(props.userDetails[getSpecialtyFields(props.activeSpecialty).trialStartDate]);
          if(trialValid){
            setSubscribe('unlock')
          } else {
            setSubscribe('lock')
          }
          setLoading(false)
        }
        else{
          setSubscribe('lock')
          setLoading(false)
        }
      }
    }
  }

  useEffect(() => {
    fetchSubscriptionDetails()
  }, [props.fetchDetailsSubscription, studentsLifetimePlan, props.userDetails, props.activeSpecialty ])

  const [isVisible, setIsVisible] = useState(true);
  
  const overlayStyle = {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      color: 'white',
      textAlign: 'center',
      padding: '10px 0',
      // display: isVisible ? 'block' : 'none',
      minWidth: "100%",
      zIndex: "400"
  };


  return (
    <>
      <Sidebar
        {...props}
        routes={routes}
        logo={{
          innerLink: "/client/question-bank/create-test",
          imgSrc: require("../assets/img/brand/pmr-logo.png"),
          imgSrcWhite: require("../assets/img/brand/pmr-logo.png"),
          imgAlt: "..."
        }}
      />
      <div className="main-content" ref={mainContent}>
        <AdminNavbar
          {...props}
        //   brandText={getBrandText(props.location.pathname)}
            name={props.userDetails.name}
        />
        <Switch>
          {getRoutes(routes)}
          <Redirect from="*" to="/client/dashboard" />
        </Switch>
        <Container fluid>
          {/* <AdminFooter /> */}
          
        {
            isLoading === false ? 
              isStudent ? 
                subscribe == "lock" && showUpgradeMessage ? 
                // <Container 
                //     style={overlayStyle}
                //     onMouseEnter={() => setIsVisible(false)}
                //     onMouseLeave={() => setIsVisible(true)}
                // >
                //     You are using a free plan. <a href="#" className={ props.adminUser.admin ? "text-yellow disabled" : "text-yellow"} onClick={e => {e.preventDefault(); history.push("/client/plans")}} >Upgrade to Pro.</a>
                // </Container>  
                <Container 
                style={overlayStyle}
                onMouseEnter={() => setIsVisible(false)}
                onMouseLeave={() => setIsVisible(true)}
                >
                    You are using a free plan. <a href="#" className={ "text-yellow"} onClick={e => {e.preventDefault(); history.push("/client/plans")}} >Upgrade to Pro.</a>
                </Container>  
                
                  : ""
                : ""
            : ""
          }
        </Container>
      </div>

      {/* Residency Program & Year Modal */}
      <Modal isOpen={showResidencyModal} centered backdrop="static" keyboard={false}>
        <ModalHeader>Complete Your Profile</ModalHeader>
        <ModalBody>
          <p className="text-muted">Help us personalize your experience</p>
          {!props.userDetails[getSpecialtyFields(props.activeSpecialty).pmrProgram] && (
            <FormGroup>
              <label className="font-weight-bold mb-1">Residency Program</label>
              <Select
                placeholder={`Select ${getSpecialty(props.activeSpecialty).name} Program`}
                options={allPmrProgramOptions}
                styles={customDropdown}
                onChange={(option) => setSelectedProgram(option)}
                value={selectedProgram}
                isClearable
                menuPortalTarget={document.body}
              />
            </FormGroup>
          )}
          {!props.userDetails[getSpecialtyFields(props.activeSpecialty).year] && (
            <FormGroup>
              <label className="font-weight-bold mb-1">PGY Year</label>
              <Select
                placeholder="Select Year"
                options={yearOptions}
                styles={customDropdown}
                onChange={(option) => setSelectedYear(option)}
                value={selectedYear}
                isClearable
                menuPortalTarget={document.body}
              />
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleResidencySkip}>Later</Button>
          <Button color="primary" onClick={handleResidencySave}>Save</Button>
        </ModalFooter>
      </Modal>

      {/* Heard About Us Modal */}
      <Modal isOpen={showHeardModal} centered backdrop="static" keyboard={false}>
        <ModalHeader>One More Thing!</ModalHeader>
        <ModalBody>
          <p className="text-muted">Where did you hear about {getSpecialty(props.activeSpecialty).name} Prep?</p>
          <FormGroup>
            <Select
              placeholder="Where did you hear about us?"
              options={heardAboutUsOptions}
              styles={customDropdown}
              onChange={(option) => {
                setSelectedHeard(option);
                if (option?.value !== 'Other') setHeardOther('');
              }}
              value={selectedHeard}
              isClearable
              menuPortalTarget={document.body}
            />
          </FormGroup>
          {selectedHeard?.value === 'Other' && (
            <FormGroup>
              <InputGroup className="input-group-alternative">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="fas fa-comment" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  placeholder="Please specify..."
                  className="form-control-alternative"
                  value={heardOther}
                  onChange={(e) => setHeardOther(e.target.value)}
                  type="text"
                />
              </InputGroup>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowHeardModal(false)}>Later</Button>
          <Button color="primary" onClick={handleHeardSave}>Save</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
const mapStateToProps = (state) => ({
    userDetails: state.loginAuth.userDetails,
    fetchDetailsSubscription : state.fetchDetailsSubscription,
    adminUser : state.adminUser,
    fetchLtpItem : state.fetchLtpItem,
    trial: state.trial,
    redeemGroupCode: state.redeemGroupCode,
    activeSpecialty: state.specialty.activeSpecialty,
})

export default connect(mapStateToProps)(Admin);
